import { RegularAd } from 'googlead/components/RegularAd';
import React, { useEffect, useState } from 'react';
import { IResortReviews, ReviewsOrderBy } from 'api/resort/types';
import { BlockContent } from 'components/blocks/BlockContent';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { Diagnostics } from 'util/diagnostics';
import { fetchResortReviewsPages } from 'api/resort/static';
import classNames from 'classnames';
import Loader from 'react-spinners/PuffLoader';
import theme from 'theme';
import { Review } from 'components/resort/Review';
import { SortReviewsRating } from 'components/resort/SortReviewsRating';
import { isValidResponse } from 'util/isValidResponse';
import styles from './styles.module.scss';

export const ReviewsList = ({
  reviewsList,
  uuid,
  className,
}: {
  reviewsList: IResortReviews,
  uuid: string,
  className?: string,
}) => {
  const { t } = useOTSTranslation('common');
  const [sortBy, setSortBy] = useState<ReviewsOrderBy>(ReviewsOrderBy.date);
  const [isLoading, toggleLoading] = useState<boolean>(false);
  const [pagesReviews, addPageReviews] = useState<Array<IResortReviews>>([reviewsList]);
  const getLastPage = Math.ceil(pagesReviews[0].pagination.count / pagesReviews[0].pagination.limit);
  const AD_EVERY_REVIEWS = 4;

  const loadNextPageReviews = async () => {
    toggleLoading(true);
    const newReviewsList: IResortReviews = await fetchResortReviewsPages(uuid, sortBy, pagesReviews.length);

    if (!isValidResponse(newReviewsList, ['pagination', 'reviewsList'])) {
      Diagnostics.error({ message: 'Server returned unknown report page type', tag: 'resort-reports' });
      return;
    }

    addPageReviews((prevPagesResort) => [...prevPagesResort, newReviewsList]);
    toggleLoading(false);
  };

  const loadSortedReviews = async () => {
    const sortedReviews = await fetchResortReviewsPages(uuid, sortBy, 0);

    if (!isValidResponse(sortedReviews, ['pagination', 'reviewsList'])) {
      Diagnostics.error({ message: 'Server returned unknown report page type', tag: 'resort-reports' });
      return;
    }

    addPageReviews([sortedReviews]);
  };

  useEffect(() => {
    loadSortedReviews();
  }, [sortBy]);

  return (
    <div className={classNames(styles.wrapper, className)}>
      <SortReviewsRating sortBy={sortBy} setSortBy={setSortBy} />
      <BlockContent className={styles.box}>
        {
          pagesReviews.map((item, pageIndex) => (
            item.reviewsList.map((review, index) => {
              const shouldInsertAd = ((pageIndex * 10) + index + 1) % AD_EVERY_REVIEWS === 0;
              return (
                <React.Fragment key={`${review.uuid}-${review.date}-${review.review}`}>
                  <Review key={`${review.uuid}-${review.date}-${review.review}`} review={review} />
                  {
                    shouldInsertAd ? (
                      <RegularAd />
                    ) : ''
                  }
                </React.Fragment>
              );
            })
          ))
        }
        {
          pagesReviews.length !== getLastPage ? (
            <button
              type="button"
              className={styles.loadBtn}
              onClick={loadNextPageReviews}
              disabled={isLoading}
            >
              {
                isLoading ? (
                  <Loader
                    color={theme.white}
                    size={32}
                  />
                ) : t('common.loadMore')
              }
            </button>
          ) : <></>
        }
      </BlockContent>
    </div>
  );
};
